<template>
  <div class="auth-container">
    <div class="auth-box card">
      <!-- Loading State -->
      <div v-if="validating" class="loading-state">
        <div class="spinner-large"></div>
        <p>Validating reset link...</p>
      </div>
      
      <!-- Invalid Token State -->
      <div v-else-if="!tokenValid" class="error-state">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h2>Invalid or Expired Link</h2>
        <p>
          {{ errorMessage || 'This password reset link is invalid or has expired.' }}
        </p>
        <p class="note">
          Password reset links expire after 24 hours for security.
        </p>
        <router-link to="/forgot-password" class="btn btn-block">
          Request New Reset Link
        </router-link>
      </div>
      
      <!-- Reset Form -->
      <div v-else-if="!resetComplete">
        <h2 class="text-center">Create New Password</h2>
        <p class="description">
          Enter a new password for <strong>{{ userEmail }}</strong>
        </p>
        
        <form @submit.prevent="handleReset">
          <div class="form-group">
            <label for="password">New Password</label>
            <div class="password-input-wrapper">
              <input 
                :type="showPassword ? 'text' : 'password'" 
                id="password" 
                v-model="password" 
                class="form-control" 
                required
                minlength="8"
                autocomplete="new-password"
                placeholder="Minimum 8 characters"
              >
              <button 
                type="button" 
                class="toggle-password"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>
          
          <div class="form-group">
            <label for="confirmPassword">Confirm New Password</label>
            <div class="password-input-wrapper">
              <input 
                :type="showConfirmPassword ? 'text' : 'password'" 
                id="confirmPassword" 
                v-model="confirmPassword" 
                class="form-control" 
                required
                autocomplete="new-password"
                placeholder="Re-enter your password"
              >
              <button 
                type="button" 
                class="toggle-password"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>
          
          <!-- Password Requirements -->
          <div class="password-requirements">
            <div :class="['requirement', { met: password.length >= 8 }]">
              <i :class="password.length >= 8 ? 'fas fa-check' : 'fas fa-times'"></i>
              At least 8 characters
            </div>
            <div :class="['requirement', { met: passwordsMatch && password.length > 0 }]">
              <i :class="passwordsMatch && password.length > 0 ? 'fas fa-check' : 'fas fa-times'"></i>
              Passwords match
            </div>
          </div>
          
          <div v-if="error" class="error-message">{{ error }}</div>
          
          <button 
            type="submit" 
            class="btn btn-block" 
            :disabled="loading || !canSubmit"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ loading ? 'Resetting...' : 'Reset Password' }}
          </button>
        </form>
      </div>
      
      <!-- Success State -->
      <div v-else class="success-state">
        <div class="success-icon">
          <i class="fas fa-check"></i>
        </div>
        <h2>Password Reset!</h2>
        <p>
          Your password has been successfully reset. You can now log in with your new password.
        </p>
        <router-link to="/login" class="btn btn-block">
          Go to Login
        </router-link>
      </div>
      
      <div class="back-link" v-if="!resetComplete">
        <router-link to="/login">
          <i class="fas fa-arrow-left"></i> Back to Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'ResetPassword',
  data() {
    return {
      uid: '',
      token: '',
      userEmail: '',
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      validating: true,
      tokenValid: false,
      loading: false,
      error: '',
      errorMessage: '',
      resetComplete: false,
    };
  },
  computed: {
    passwordsMatch() {
      return this.password === this.confirmPassword;
    },
    canSubmit() {
      return this.password.length >= 8 && this.passwordsMatch;
    }
  },
  async mounted() {
    // Get uid and token from URL query params
    this.uid = this.$route.query.uid || '';
    this.token = this.$route.query.token || '';
    
    if (!this.uid || !this.token) {
      this.validating = false;
      this.tokenValid = false;
      this.errorMessage = 'Missing reset link parameters. Please use the link from your email.';
      return;
    }
    
    // Validate the token
    await this.validateToken();
  },
  methods: {
    async validateToken() {
      try {
        const response = await api.post('/users/password-reset/validate/', {
          uid: this.uid,
          token: this.token
        });
        
        if (response.data.valid) {
          this.tokenValid = true;
          this.userEmail = response.data.email;
        } else {
          this.tokenValid = false;
          this.errorMessage = response.data.error || 'Invalid or expired reset link.';
        }
      } catch (error) {
        this.tokenValid = false;
        this.errorMessage = error.response?.data?.error || 'Failed to validate reset link.';
      } finally {
        this.validating = false;
      }
    },
    
    async handleReset() {
      if (!this.canSubmit) return;
      
      this.loading = true;
      this.error = '';
      
      try {
        await api.post('/users/password-reset/confirm/', {
          uid: this.uid,
          token: this.token,
          password: this.password
        });
        
        this.resetComplete = true;
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to reset password. Please try again.';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--color-surface);
}

.auth-box {
  width: 100%;
  max-width: 420px;
}

.text-center {
  text-align: center;
}

.description {
  color: #6b7280;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
}

label {
  display: block;
  margin-bottom: var(--spacing-sm);
  font-weight: 600;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: 3rem;
  border: 1px solid #e1e5e9;
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

.password-input-wrapper {
  position: relative;
}

.toggle-password {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  color: #6b7280;
}

.password-requirements {
  background: #f9fafb;
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1.5rem;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #9ca3af;
  margin-bottom: 0.5rem;
}

.requirement:last-child {
  margin-bottom: 0;
}

.requirement i {
  width: 1rem;
  text-align: center;
}

.requirement.met {
  color: #10b981;
}

.requirement.met i {
  color: #10b981;
}

.btn {
  width: 100%;
  padding: 0.875rem 1rem;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-block {
  background-color: var(--color-primary, #1E40AF);
  color: white;
}

.btn-block:hover:not(:disabled) {
  background-color: var(--color-primary, #1E40AF);
  filter: brightness(110%);
}

.btn-block:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  color: #dc2626;
  background: #fef2f2;
  padding: 0.75rem;
  border-radius: var(--border-radius);
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 3rem 1rem;
}

.spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: var(--color-primary, #1E40AF);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

.loading-state p {
  color: #6b7280;
}

/* Error State */
.error-state {
  text-align: center;
  padding: 1rem 0;
}

.error-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.error-icon i {
  font-size: 2rem;
  color: white;
}

.error-state h2 {
  color: #111827;
  margin-bottom: 1rem;
}

.error-state p {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 0.5rem;
}

.error-state .note {
  font-size: 0.875rem;
  color: #9ca3af;
  margin-bottom: 1.5rem;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 1rem 0;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.success-icon i {
  font-size: 2rem;
  color: white;
}

.success-state h2 {
  color: #111827;
  margin-bottom: 1rem;
}

.success-state p {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.back-link {
  margin-top: 2rem;
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #e1e5e9;
}

.back-link a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.2s ease;
}

.back-link a:hover {
  color: var(--color-primary, #1E40AF);
}

.spinner-border {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
